import * as A from 'fp-ts/Array';
import { pipe } from 'fp-ts/lib/function';
import * as O from 'fp-ts/Option';
import { TFunction } from 'i18next';
import { curry, isEmpty, some, uniqBy } from 'lodash';
import * as R from 'ramda';

import { ROTARACT_ADVISOR } from '@domain/clubs';

import { isActiveLeadership, LeadershipDate } from '@utils/leadershipUtils';

import {
  CLUB_EXECUTIVE_SECRETARY,
  DISTRICT_EXECUTIVE_SECRETARY,
  MEMBERSHIP_STATUSES,
  MULTI_DISTRICT_EXECUTIVE_SECRETARY,
  SPECIAL_REPRESENTATIVE,
} from '@domui-domain/districts/constants';
import { getClubTypeName } from '@domui-utils/getClubTypeName';

import { Affiliation, ClubAffiliation, MembershipType } from './types';

const isLeadershipAffiliation = (
  affiliation: Affiliation
): affiliation is Affiliation =>
  (affiliation as Affiliation).role !== undefined;

const isMembershipAffiliation = (
  affiliation: Affiliation
): affiliation is Affiliation =>
  (affiliation as Affiliation).role !== undefined;

const getLeadershipAffiliations = curry(
  (role: string, affiliations: Affiliation[]): Affiliation[] =>
    pipe(
      affiliations,
      A.filter(isLeadershipAffiliation),
      A.filter(({ role: leaderRole }) => leaderRole === role)
    )
);

export const areLeadershipAffiliations = (
  affiliations: Affiliation[],
  role: string
): boolean => pipe(affiliations, getLeadershipAffiliations(role), some);

const areMembershipAffiliations = (affiliations: Affiliation[]): boolean =>
  pipe(affiliations, A.filter(isMembershipAffiliation), some);

export const getUserClubs = (
  clubAffiliations: ClubAffiliation[],
  isDistrictView?: boolean
): ClubAffiliation[] =>
  pipe(
    clubAffiliations,
    A.filter(({ type }) =>
      ['Rotary Club', 'Rotaract Club', 'District'].includes(type)
    ),
    A.filter(({ affiliation }) => {
      if (!Array.isArray(affiliation)) return false;

      let areSpecRepClubs = false;
      let areCesUserClubs = false;
      let areDESClubs = false;

      areCesUserClubs = pipe(
        affiliation,
        getLeadershipAffiliations(CLUB_EXECUTIVE_SECRETARY),
        A.head,
        O.isSome
      );

      const areMemberships = areMembershipAffiliations(affiliation);

      const areRotaractAdvisorClubs = areLeadershipAffiliations(
        affiliation,
        ROTARACT_ADVISOR
      );

      // Get clubs associated with Multi District Executive Secretary
      // for both /my-districts page and /my-clubs page.
      const areMemberOrMultiDESUserClubs = areLeadershipAffiliations(
        affiliation,
        MULTI_DISTRICT_EXECUTIVE_SECRETARY
      );

      // Get clubs associated with for Special Representative.
      // Only for districts menu items and /my-districts page.
      // Not for clubs menu items and /my-clubs page
      if (isDistrictView) {
        areSpecRepClubs = areLeadershipAffiliations(
          affiliation,
          SPECIAL_REPRESENTATIVE
        );

        areDESClubs = areLeadershipAffiliations(
          affiliation,
          DISTRICT_EXECUTIVE_SECRETARY
        );
      }

      return (
        areCesUserClubs ||
        areMemberOrMultiDESUserClubs ||
        areSpecRepClubs ||
        areMemberships ||
        areRotaractAdvisorClubs ||
        areDESClubs
      );
    })
  );

export const getDESOnlyClubs = (
  clubAffiliations: ClubAffiliation[]
): ClubAffiliation[] =>
  pipe(
    clubAffiliations,
    A.filter(({ type }) => ['District'].includes(type)),
    A.filter(({ affiliation }) => {
      if (!Array.isArray(affiliation)) return false;
      const areDESClubs = areLeadershipAffiliations(
        affiliation,
        DISTRICT_EXECUTIVE_SECRETARY
      );
      return areDESClubs;
    })
  );

export const isActiveDistrict = (affiliations: Affiliation[]) => {
  return affiliations?.some(affiliation =>
    isActiveLeadership(affiliation as LeadershipDate)
  );
};

export const getExecutiveSecrataryClubs = (
  clubAffiliations: ClubAffiliation[]
): ClubAffiliation[] => {
  // Collect all districts from club affiliations
  const allDistricts = clubAffiliations?.filter(({ type }) =>
    ['District'].includes(type)
  );

  // Fetch CES/MDES District Ids from districts
  const DESDistricts = allDistricts
    ?.filter(({ affiliation }) => {
      return (
        !isEmpty(affiliation) &&
        Array.isArray(affiliation) &&
        (areLeadershipAffiliations(affiliation, CLUB_EXECUTIVE_SECRETARY) ||
          areLeadershipAffiliations(
            affiliation,
            MULTI_DISTRICT_EXECUTIVE_SECRETARY
          )) &&
        isActiveDistrict(affiliation)
      );
    })
    .map(({ riClubId }) => riClubId || '')
    .filter(Boolean);
  if (isEmpty(DESDistricts)) {
    return [];
  }

  // Collect all clubs from club affiliations
  const allClubs = clubAffiliations?.filter(({ type }) =>
    ['Rotary Club', 'Rotaract Club'].includes(type)
  );

  // Filter clubs that has DES/CES district associated.
  const ESClubs = allClubs
    ?.filter(club => {
      const { district } = club;
      const DESClubDistrict = district?.filter(clubDistrict =>
        DESDistricts.includes(clubDistrict?.riDistrictId)
      );
      return !isEmpty(DESClubDistrict);
    })
    ?.sort((a, b) => a.name.localeCompare(b.name));

  return !isEmpty(ESClubs) ? uniqBy(ESClubs, 'riClubId') : [];
};

export const getDESClubs = (
  clubAffiliations: ClubAffiliation[]
): ClubAffiliation[] => {
  // Collect all districts from club affiliations
  const allDistricts = clubAffiliations?.filter(({ type }) =>
    ['District'].includes(type)
  );

  // Fetch DES District Ids from districts
  const DESDistricts = allDistricts
    ?.filter(({ affiliation }) => {
      return (
        !isEmpty(affiliation) &&
        Array.isArray(affiliation) &&
        areLeadershipAffiliations(affiliation, DISTRICT_EXECUTIVE_SECRETARY) &&
        isActiveDistrict(affiliation)
      );
    })
    .map(({ riClubId }) => riClubId || '')
    .filter(Boolean);
  if (isEmpty(DESDistricts)) {
    return [];
  }

  // Collect all clubs from club affiliations
  const allClubs = clubAffiliations?.filter(({ type }) =>
    ['Rotary Club', 'Rotaract Club'].includes(type)
  );

  // Filter clubs that has DES/CES district associated.
  const DESClubs = allClubs
    ?.filter(club => {
      const { district } = club;
      const DESClubDistrict = district?.filter(clubDistrict =>
        DESDistricts.includes(clubDistrict?.riDistrictId)
      );
      return !isEmpty(DESClubDistrict);
    })
    ?.sort((a, b) => a.name.localeCompare(b.name));

  return !isEmpty(DESClubs) ? uniqBy(DESClubs, 'riClubId') : [];
};

export const processOnlyDESClubs = (
  clubAffiliations: ClubAffiliation[],
  DESDistrict: ClubAffiliation[]
): ClubAffiliation[] => {
  // If Non-DES Individual, return empty clubs
  if (isEmpty(DESDistrict)) {
    return [] as ClubAffiliation[];
  }

  // Check if Individual is Member or Honarary
  const memberOrHonararyClubs = clubAffiliations?.filter(
    ({ affiliation }) =>
      !isEmpty(affiliation) &&
      Array.isArray(affiliation) &&
      affiliation.some(aff => MEMBERSHIP_STATUSES.includes(aff?.role))
  );

  // If DES Individual is Non-Member, return DES Clubs
  if (isEmpty(memberOrHonararyClubs)) {
    return getDESClubs(clubAffiliations);
  }

  // If DES Individual is Member, return empty clubs
  return [] as ClubAffiliation[];
};

export const isDESNonMember = (
  clubAffiliations: ClubAffiliation[],
  DESDistrict: ClubAffiliation[]
): Boolean => {
  // If Non-DES Individual, return false
  if (isEmpty(DESDistrict)) {
    return false;
  }

  // Check if Indivdual is Member or Honarary
  const memberOrHonararyClubs = clubAffiliations?.filter(
    ({ affiliation }) =>
      !isEmpty(affiliation) &&
      Array.isArray(affiliation) &&
      affiliation.some(aff => MEMBERSHIP_STATUSES.includes(aff?.role))
  );

  // If DES Member not found, return true
  return isEmpty(memberOrHonararyClubs);
};

export const getClubLeadership = (clubs: ClubAffiliation[], t: TFunction) =>
  clubs.map((club: ClubAffiliation) => ({
    ...club,
    formattedClubType: getClubTypeName(t, club.type),
  }));

export const sortClubAffiliations = (
  items: ClubAffiliation[]
): ClubAffiliation[] => {
  const getWeight = (item: ClubAffiliation) => {
    const isMember = item.affiliation.find(
      a =>
        a.role === MembershipType.Member || a.role === MembershipType.Rotaractor
    );
    if (isMember) {
      return 1;
    }

    return 2;
  };

  return R.sort((a, b) => {
    const aWeight = getWeight(a);
    const bWeight = getWeight(b);

    if (aWeight < bWeight) return -1;

    if (aWeight > bWeight) return 1;

    if (a.name < b.name) return -1;

    if (a.name > b.name) return 1;

    return 0;
  }, items);
};
